import type {
  DoctorSignatureMode,
  GenerateCompanyContractInput,
} from '@shared/schemas'
import type { AxiosRequestConfig } from 'axios'

export interface ContractApiErrorPayload {
  statusCode?: number
  message?: string | Array<string>
  fieldErrors?: Record<string, Array<string> | undefined>
}

export class ContractApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly fieldErrors: Record<string, Array<string> | undefined> = {},
  ) {
    super(message)
    this.name = 'ContractApiError'
  }
}

export interface CompanyContractRequest {
  companyId: string
}

export interface GenerateCompanyContractRequest extends CompanyContractRequest {
  input: GenerateCompanyContractInput
  doctorSignatureMode: DoctorSignatureMode
  doctorSignature?: File
}

export interface SendCompanyNominationEmailResult {
  recipient: string
  sentAt: string | Date
}

type RequestHeaders = AxiosRequestConfig['headers']

function resolveCompanyUrl(url: string, companyId: string): string {
  return url.replace(':companyId', encodeURIComponent(companyId))
}

function headersWithoutContentType(headers?: RequestHeaders): HeadersInit {
  const normalized = {
    ...(headers as Record<string, string> | undefined),
  }
  delete normalized['Content-Type']
  delete normalized['content-type']
  return normalized
}

export async function generateCompanyContract(
  url: string,
  method: string,
  request: GenerateCompanyContractRequest,
  headers?: RequestHeaders,
): Promise<Blob> {
  const body = new FormData()
  body.append('input', JSON.stringify(request.input))
  body.append('doctorSignatureMode', request.doctorSignatureMode)
  if (request.doctorSignature) {
    body.append('doctorSignature', request.doctorSignature)
  }
  const response = await fetch(resolveCompanyUrl(url, request.companyId), {
    method,
    headers: headersWithoutContentType(headers),
    body,
  })
  if (!response.ok) await throwContractError(response)
  return response.blob()
}

export async function downloadCompanyContract(
  url: string,
  _method: string,
  request: CompanyContractRequest,
  headers?: RequestHeaders,
): Promise<Blob> {
  const response = await fetch(resolveCompanyUrl(url, request.companyId), {
    headers: headers as HeadersInit,
  })
  if (!response.ok) await throwContractError(response)
  return response.blob()
}

export async function downloadCompanyContractDocx(
  url: string,
  _method: string,
  request: CompanyContractRequest,
  headers?: RequestHeaders,
): Promise<Blob> {
  const response = await fetch(resolveCompanyUrl(url, request.companyId), {
    headers: headers as HeadersInit,
  })
  if (!response.ok) await throwContractError(response)
  return response.blob()
}

async function throwContractError(response: Response): Promise<never> {
  const payload = (await response
    .json()
    .catch(() => ({}))) as ContractApiErrorPayload
  const message = Array.isArray(payload.message)
    ? payload.message.join(', ')
    : payload.message || `Richiesta non riuscita (${response.status})`
  throw new ContractApiError(message, response.status, payload.fieldErrors)
}
